/**
 * react-router ``errorElement`` for every route (REQ-UI-191).
 *
 * A data router renders a route's ``errorElement`` in place of its element
 * when the element throws during render. Declared on a *child* route, that
 * replaces only the parent's ``<Outlet />`` — so a crash in the workspace
 * leaves :class:`AppShell`'s top bar, banners and toasts standing, which is
 * the "keep the chrome" half of the requirement. Declared on the layout
 * route as well, it is the last line before react-router's default
 * "Unexpected Application Error!" page and its minified stack, which must
 * never reach an operator (REQ-UI-037).
 *
 * Retry here is a page reload: react-router clears a route error only on
 * navigation, and the index route *is* the destination, so a client-side
 * re-navigation cannot recover it. The login callback route instead offers
 * a link to ``/``, which restarts the OIDC flow rather than replaying a
 * spent authorization code.
 *
 * Cross-references: REQ-UI-191; REQ-UI-037; Flow M Frame 5;
 * :file:`routes.tsx`.
 */
import { useEffect } from "react";
import { useRouteError } from "react-router";
import { describeError } from "@/chrome/describe-error";
import {
  ErrorFallback,
  type ErrorFallbackAction,
  type ErrorFallbackScope,
} from "@/chrome/error-fallback";

export interface RouteErrorFallbackProps {
  /** The route's surface in operator words — "workspace", "sign-in page", "Meridian". */
  surface: string;
  /**
   * ``canvas`` (default) for a child route, whose crash leaves the shell
   * standing; ``page`` for the layout route itself, where nothing else
   * survives and the copy must not claim otherwise.
   */
  scope?: Extract<ErrorFallbackScope, "canvas" | "page">;
  /** Override the default "Reload page" action (the callback route links home). */
  primaryAction?: ErrorFallbackAction;
  /** Injectable page reload (defaults to ``window.location.reload``). */
  reloadPage?: () => void;
}

/**
 * Render the failure card for the route that threw.
 *
 * Reads the thrown value via :func:`useRouteError`; nothing else about the
 * error is interpreted — there are no loaders, so no ``ErrorResponse``
 * branch is needed.
 */
export function RouteErrorFallback({
  surface,
  scope = "canvas",
  primaryAction,
  reloadPage,
}: RouteErrorFallbackProps): React.ReactElement {
  const error = useRouteError();
  const reload = reloadPage ?? ((): void => window.location.reload());
  useEffect(() => {
    // Same grep-able line :class:`ErrorBoundary` emits, so a console
    // capture names the surface whichever boundary caught the error.
    // react-router has already logged the error itself.
    console.error(
      `[meridian-ui] render error contained in ${surface} (route): ${describeError(error).summary}`,
    );
  }, [surface, error]);
  return (
    <ErrorFallback
      scope={scope}
      surface={surface}
      error={error}
      primaryAction={primaryAction ?? { label: "Reload page", onClick: reload }}
      testId={`route-error-${surface.replace(/\s+/g, "-").toLowerCase()}`}
    />
  );
}
